import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { ScanBatch, ScanListResponse } from '../types/scan';

interface UseScanBatchesResult {
  batches: ScanBatch[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetch the recent (non-queued) scan batches for an application. Powers the scan filter dropdown
 * on the Findings page, so users can see which scan they're filtered to and switch between scans.
 */
export function useScanBatches(applicationId?: string | null): UseScanBatchesResult {
  const api = useAuthenticatedApi();
  const [batches, setBatches] = useState<ScanBatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBatches = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<ScanListResponse>(
        apiEndpoints.scans.listBatches(applicationId || undefined),
      );
      // Queued batches have no findings yet, so they're not useful as a findings filter.
      // Defensive: never let a malformed response crash consumers that map over batches.
      setBatches(Array.isArray(response?.batches) ? response.batches : []);
    } catch (err: any) {
      console.error('Error fetching scan batches:', err);
      setError(err?.message || 'Failed to load scans');
      setBatches([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, applicationId]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  return { batches, isLoading, error };
}

export default useScanBatches;
